import axiosInstance from '../../../axiosInstance';
import apiEndpoints from '../../../api';
import { notify } from '../../../../utils/notification';

// Joi runs in non-stripping mode on every visitor route, so an unexpected key
// is a 400 rather than a silently ignored field. Every request below therefore
// builds its params/body from an explicit whitelist and drops empties.
const clean = (obj) => {
    const out = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    return out;
};

// Validation failures bypass the API's response helper and come back as
// { status: 'error', message: 'body: "name" is required' } instead of the usual
// { success: false, message }. Both shapes carry `message`, so one reader
// covers them.
const errMessage = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

// The controller scopes the list on req.query.society_id and falls back to
// req.societyId, which the backend never assigns — so the X-Society-Id header
// alone authorises the call but does NOT scope the data. society_id has to ride
// in the query (and in the create body) explicitly or the list leaks every
// society's visitors.
const listParams = (data) => clean({
    society_id: data?.societyId,
    page: data?.page || 1,
    limit: data?.limit || 10,
    search: data?.search,
    visitor_type: data?.visitorType,
    // Sent as a string so the boolean `false` survives clean()'s empty check.
    is_blocked: data?.isBlocked === undefined || data?.isBlocked === null || data?.isBlocked === ''
        ? undefined
        : String(data.isBlocked),
});

export const getVisitorsListReq = (action) => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_VISITORS_LIST_API, { params: listParams(action.data) })
        .then(res => res)
        .catch((err) => {
            notify('error', errMessage(err, 'Failed to load visitors'));
            return err;
        });
};

// Counter probes for the list banner: limit=1 so only meta.pagination.total
// matters. Silent on failure — a missing badge should never nag the user.
export const getVisitorCountReq = (societyId, extra = {}) => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_VISITORS_LIST_API, {
        params: clean({ society_id: societyId, page: 1, limit: 1, ...extra }),
    })
        .then(res => res)
        .catch((err) => err);
};

export const getVisitorDetailsReq = (action) => {
    const url = apiEndpoints.SOCIETY_GET_VISITOR_DETAILS_API.replace(':id', action.data.id);
    return axiosInstance.get(url)
        .then(res => res)
        .catch((err) => {
            notify('error', errMessage(err, 'Failed to load visitor'));
            return err;
        });
};

export const createVisitorReq = (action) => {
    const { onSuccess, onError, ...rest } = action.data || {};
    return axiosInstance.post(apiEndpoints.SOCIETY_CREATE_VISITOR_API, clean(rest))
        .then(res => {
            if (res?.success) notify('success', res.message || 'Visitor registered');
            return res;
        })
        .catch((err) => {
            // 409 means the phone is already taken in this society — including by
            // a soft-deleted visitor, since the uniqueness lookup ignores
            // is_deleted and there is no restore endpoint. Say "already
            // registered" rather than inviting a retry that can never succeed.
            const msg = err?.response?.status === 409
                ? (errMessage(err) || 'This number is already registered in this society')
                : errMessage(err, 'Failed to register visitor');
            notify('error', msg);
            return err;
        });
};

export const updateVisitorReq = (action) => {
    const url = apiEndpoints.SOCIETY_UPDATE_VISITOR_API.replace(':id', action.data.id);
    // society_id is rejected on update — a visitor cannot move between
    // societies — and id belongs in the path, not the body.
    const { id, society_id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.put(url, payload)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Visitor updated');
            return res;
        })
        .catch((err) => {
            const msg = err?.response?.status === 409
                ? (errMessage(err) || 'This number is already registered in this society')
                : errMessage(err, 'Failed to update visitor');
            notify('error', msg);
            return err;
        });
};

export const deleteVisitorReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELETE_VISITOR_API.replace(':id', action.data.id);
    return axiosInstance.delete(url)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Visitor removed');
            return res;
        })
        .catch((err) => {
            notify('error', errMessage(err, 'Failed to remove visitor'));
            return err;
        });
};

// reason is optional (<= 255) and the body may legitimately be {}.
export const blockVisitorReq = (action) => {
    const url = apiEndpoints.SOCIETY_BLOCK_VISITOR_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.post(url, clean(payload))
        .then(res => {
            if (res?.success) notify('success', res.message || 'Visitor blocked');
            return res;
        })
        .catch((err) => {
            notify('error', errMessage(err, 'Failed to block visitor'));
            return err;
        });
};

export const unblockVisitorReq = (action) => {
    const url = apiEndpoints.SOCIETY_UNBLOCK_VISITOR_API.replace(':id', action.data.id);
    return axiosInstance.post(url, {})
        .then(res => {
            if (res?.success) notify('success', res.message || 'Visitor unblocked');
            return res;
        })
        .catch((err) => {
            notify('error', errMessage(err, 'Failed to unblock visitor'));
            return err;
        });
};
